/**
 * Deployment-mode resolution for the dynamic mode indicator (Phase 8.4
 * leftover · CD R9 Q10 verdict: "dynamic mode indicator — not deleted —
 * provenance").
 *
 * The mode chip in the top bar tells the operator which tier of data
 * they're looking at. Replaces the static "EDU · NOT FOR OPS" chip.
 *
 * Modes (worst-known → best-known):
 *   sample        — illustrative / synthetic data; not from live ingest
 *   edu           — engineering prototype default (live ingest mixed
 *                   with sample fallbacks); explicitly not for ops use
 *   staging       — live ingest, full pipeline, but staging tenant /
 *                   non-production data path
 *   prod-tenant   — production deployment for a paying tenant; live
 *                   data, audit-traceable, governance-gated
 *
 * Resolution priority:
 *   1. `import.meta.env.VITE_PACC_MODE` (set per deployment in CI / env)
 *   2. Default to `edu` (the prototype's safe default)
 *
 * Per CD R9 verdict, the chip is NEVER hidden — even prod-tenant gets a
 * subtle "PROD" indicator. This is provenance, not a warning.
 */

export type DeploymentMode = 'sample' | 'edu' | 'staging' | 'prod-tenant';

const VALID: ReadonlySet<DeploymentMode> = new Set(['sample', 'edu', 'staging', 'prod-tenant']);

function resolveMode(): DeploymentMode {
  const raw = (import.meta.env.VITE_PACC_MODE ?? '').toLowerCase().trim();
  if (VALID.has(raw as DeploymentMode)) return raw as DeploymentMode;
  return 'edu';
}

export const DEPLOYMENT_MODE: DeploymentMode = resolveMode();

interface ModeMeta {
  word: string;
  icon: string;
  /** Inline-style colour token name (CSS var). */
  colorVar: string;
  /** Background tint token name (CSS var). */
  bgVar: string;
  /** Border token name (CSS var). */
  borderVar: string;
  /** Tooltip text shown on hover — the operator's "what does this mean?" rail. */
  detail: string;
}

export const MODE_META: Record<DeploymentMode, ModeMeta> = {
  sample: {
    word: 'SAMPLE',
    icon: '◇',
    colorVar: 'var(--bm-prov-synthetic, var(--bm-state-stale))',
    bgVar: 'var(--bm-prov-synthetic-bg, var(--bm-state-stale-bg))',
    borderVar: 'var(--bm-prov-synthetic, var(--bm-state-stale))',
    detail: 'Illustrative sample data — not from live ingest. Use to explore surfaces; do NOT cite numbers.',
  },
  edu: {
    word: 'EDU · NOT FOR OPS',
    icon: '⚠',
    colorVar: 'var(--bm-state-stale, #fbbf24)',
    bgVar: 'var(--bm-prov-stale-bg, rgba(251,191,36,0.14))',
    borderVar: 'var(--bm-state-stale, #fbbf24)',
    detail: 'Engineering prototype — synthetic + open-source surveillance signals only. Do NOT use for operational outbreak response.',
  },
  staging: {
    word: 'STAGING',
    icon: '◷',
    colorVar: 'var(--bm-accent, #6ea3ff)',
    bgVar: 'var(--bm-accent-subtle, rgba(110,163,255,0.14))',
    borderVar: 'var(--bm-accent, #6ea3ff)',
    detail: 'Live ingest pipeline, staging tenant — not the production data path. OK for operator dry-runs; do not brief from here.',
  },
  'prod-tenant': {
    word: 'PROD',
    icon: '●',
    colorVar: 'var(--bm-prov-confirmed, var(--bm-state-live))',
    bgVar: 'var(--bm-state-live-bg, rgba(52,211,153,0.12))',
    borderVar: 'var(--bm-prov-confirmed, var(--bm-state-live))',
    detail: 'Production deployment — live data, audit-traceable, governance-gated. Briefings shipped from this mode carry operational weight.',
  },
};
